/** Loads one wallet queue at a time and ignores responses from prior wallets. */
/* eslint-disable import/order -- CLAUDE.md requires external, workspace, absolute, then relative imports. */
import { useCallback, useEffect, useRef, useState } from "react";

import { captureWalletEvent, eventId, type EventOwner, ownerForAddress } from "@shared/analytics/walletEvents";
import { DEFAULT_SALT } from "@solomon-apps/shared";
import type { PublicKey } from "@solomon-labs/publickey";
import { migrationContext } from "@/services/usdv-migration/analytics";

import { type StakingStateLoadResult, useStakingSessionStore } from "@/stores/useStakingSessionStore";

import { isCurrentUsdvMigrationQueueLoad, type UsdvMigrationQueueLoadRequest } from "./queue";

export type UsdvMigrationQueueLoadStatus = "idle" | "loading" | "loaded" | "error" | "retrying";

interface UseUsdvMigrationQueueOptions {
    owner: PublicKey | null | undefined;
}

interface UseUsdvMigrationQueueResult {
    owner: string | null;
    status: UsdvMigrationQueueLoadStatus;
    retry: () => void;
    refreshAfterClaim: (owner: PublicKey) => Promise<void>;
}

export function useUsdvMigrationQueue({ owner }: UseUsdvMigrationQueueOptions): UseUsdvMigrationQueueResult {
    const loadStakingState = useStakingSessionStore((store) => store.getStakingState);
    const refreshStakingState = useStakingSessionStore((store) => store.refreshStakingState);
    const [queueOwner, setQueueOwner] = useState<string | null>(null);
    const [status, setStatus] = useState<UsdvMigrationQueueLoadStatus>("idle");
    const requestRef = useRef<UsdvMigrationQueueLoadRequest>({
        owner: null,
        generation: 0
    });

    const checks = useRef(new Map<number, { id: string; started: number; owner: EventOwner }>());
    const beginRequest = useCallback((requestOwner: PublicKey, trigger: "initial" | "retry" | "refresh") => {
        const request = {
            owner: requestOwner.toString(),
            generation: requestRef.current.generation + 1
        };
        requestRef.current = request;
        const check = { id: eventId(), started: performance.now(), owner: ownerForAddress(request.owner) };
        checks.current.set(request.generation, check);
        captureWalletEvent(
            "migration:check_started",
            { ...migrationContext(request.owner), check_id: check.id, check_type: "queue", trigger },
            check.owner
        );
        setQueueOwner(null);
        return request;
    }, []);
    const settleRequest = useCallback(
        (request: UsdvMigrationQueueLoadRequest, result: StakingStateLoadResult): void => {
            const check = checks.current.get(request.generation);
            if (check) {
                checks.current.delete(request.generation);
                const properties = {
                    ...migrationContext(request.owner),
                    check_id: check.id,
                    check_type: "queue" as const,
                    duration_ms: performance.now() - check.started
                };
                if (result === "loaded")
                    captureWalletEvent("migration:check_completed", { ...properties, result: "loaded" }, check.owner);
                else
                    captureWalletEvent(
                        "migration:check_failed",
                        { ...properties, error_code: "queue_load_failed", error_category: "network" },
                        check.owner
                    );
            }
            if (!isCurrentUsdvMigrationQueueLoad(request, requestRef.current)) return;

            if (result === "loaded") {
                setQueueOwner(request.owner);
                setStatus("loaded");
                return;
            }

            setStatus("error");
        },
        []
    );

    const load = useCallback(
        async (requestOwner: PublicKey, isRetry = false): Promise<void> => {
            const request = beginRequest(requestOwner, isRetry ? "retry" : "initial");
            setStatus(isRetry ? "retrying" : "loading");
            const result = await loadStakingState(requestOwner, DEFAULT_SALT);
            settleRequest(request, result);
        },
        [beginRequest, loadStakingState, settleRequest]
    );

    useEffect(() => {
        const ownerKey = owner?.toString() ?? null;
        if (!owner) {
            requestRef.current = {
                owner: null,
                generation: requestRef.current.generation + 1
            };
            setQueueOwner(null);
            setStatus("idle");
            return;
        }
        if (requestRef.current.owner === ownerKey) return;

        void load(owner);
    }, [load, owner]);

    const retry = useCallback((): void => {
        if (owner) {
            captureWalletEvent(
                "migration:retry_clicked",
                { ...migrationContext(owner.toString()), retry_target: "queue" },
                ownerForAddress(owner.toString())
            );
            void load(owner, true);
        }
    }, [load, owner]);

    const refreshAfterClaim = useCallback(
        async (requestOwner: PublicKey): Promise<void> => {
            const request = beginRequest(requestOwner, "refresh");
            setStatus("retrying");
            const result = await refreshStakingState(requestOwner, DEFAULT_SALT);
            settleRequest(request, result);
        },
        [beginRequest, refreshStakingState, settleRequest]
    );

    return { owner: queueOwner, status, retry, refreshAfterClaim };
}
